import React from 'react';
import { AlertTriangle } from 'lucide-react';

export type MessageFormatStyles = {
  boldText: string;
  citationLink: string;
  inlineLink: string;
  messageSpacer: string;
  numberedList: string;
  listItem: string;
  listIndex: string;
  listContent: string;
  messageHeader: string;
  alertBox: string;
  alertIcon: string;
  alertContent: string;
  noteBox: string;
  noteContent: string;
  labelRow: string;
  labelText: string;
  labelSeparator: string;
  labelValue: string;
  chatBubbleParagraph: string;
  messageLine: string;
};

type OrderedListBlock = {
  kind: 'orderedList';
  items: string[];
};

type UnorderedListBlock = {
  kind: 'unorderedList';
  items: string[];
};

type SpacerBlock = {
  kind: 'spacer';
};

type HeaderBlock = {
  kind: 'header';
  content: string;
};

type AlertBlock = {
  kind: 'alert';
  content: string;
};

type NoteBlock = {
  kind: 'note';
  content: string;
};

type LabelValueBlock = {
  kind: 'labelValue';
  label: string;
  value: string;
};

type ParagraphBlock = {
  kind: 'paragraph';
  content: string;
};

type MessageBlock =
  | OrderedListBlock
  | UnorderedListBlock
  | SpacerBlock
  | HeaderBlock
  | AlertBlock
  | NoteBlock
  | LabelValueBlock
  | ParagraphBlock;

const citationTokenPattern = /(\*\*[^*]+\*\*|\[Source\s+\d+\]|\[Sources\s+\d+\s*&\s*\d+\])/g;
const markdownLinkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
const markdownBoldPattern = /\*\*([^*]+)\*\*/g;
const headerLinePattern = /^(?:#{2,6}\s+|Header:\s+)(.+)$/;
const compactHeaderMaxLength = 64;
const alertLinePattern = /^(?:ALERT:\s+|!!!\s+)(.+)$/;
const noteLinePattern = /^(?:Scope note|Coverage note|Coverage|Limitations?|Note):\s+(.+)$/i;
const orderedListItemPattern = /^(\d+)\.\s+(.+)$/;
const orderedListMarkerOnlyPattern = /^(\d+)\.\s*$/;
const unorderedListItemPattern = /^(?:-\s*\[\s*\]\s*|\[\s*\]\s*|-\s+)(.+)$/;
const unorderedListMarkerOnlyPattern = /^(?:-\s*\[\s*\]|\[\s*\]|-)\s*$/;
const labelOnlyPattern = /^([A-Za-z][A-Za-z0-9/&()' -]{0,39}):$/;
const inlineLabelBoundaryPattern = /\s+([A-Z][A-Za-z0-9/&()']*(?:\s+[A-Z][A-Za-z0-9/&()']*){0,5}:)(?=\s+\S)/g;
const rawUrlPattern = /^https?:\/\//i;
const machinePayloadKeyPattern = /"(answer|citations|actions|debug|tenantStatus|dataSourceStatus|recommendation)"\s*:/i;
const machinePayloadLinePattern = /^\s*("?(answer|citations|actions|debug|tenantStatus|dataSourceStatus|recommendation)"?\s*:|[{}\[\]],?)\s*$/i;
const codeFencePattern = /```(?:json|javascript|js|txt)?\s*([\s\S]*?)```/gi;

const isSafeHref = (href: string) => (
  href.startsWith('/') || /^https?:\/\//.test(href)
);

const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

const trimLine = (value: string): string => collapseWhitespace(value.replace(/\u00A0/g, ' '));

const splitInlineLabelBoundaries = (text: string): string => text
  .split('\n')
  .map((line) => {
    const trimmed = trimLine(line);
    if (
      !trimmed
      || orderedListItemPattern.test(trimmed)
      || unorderedListItemPattern.test(trimmed)
    ) {
      return line;
    }
    return line.replace(inlineLabelBoundaryPattern, '\n$1');
  })
  .join('\n');

const rehydrateCollapsedStructure = (text: string): string => {
  const withStructuralBreaks = text
    .replace(/\s+(#{2,6}\s+)/g, '\n$1')
    .replace(/\s+(-\s*(?:\[\s*\]\s*)?)(?=[A-Za-z0-9])/g, '\n$1')
    .replace(/\s+(\d{1,2}\.\s+)(?=[A-Z])/g, '\n$1');
  return splitInlineLabelBoundaries(withStructuralBreaks);
};

const looksLikeMachinePayload = (value: string): boolean => {
  const trimmed = trimLine(value);
  if (!trimmed) {
    return false;
  }
  if (machinePayloadKeyPattern.test(trimmed)) {
    return true;
  }
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    return true;
  }
  return trimmed
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .every((line) => machinePayloadLinePattern.test(line) || /^".*"$/.test(line));
};

const stripMachinePayloadArtifacts = (text: string): string => {
  let next = text.replace(codeFencePattern, (_match, inner) => {
    const content = String(inner ?? '').trim();
    return looksLikeMachinePayload(content) ? '\n' : content;
  });
  next = next
    .split('\n')
    .filter((line) => !machinePayloadLinePattern.test(line.trim()))
    .join('\n');
  return next.replace(/\n{3,}/g, '\n\n').trim();
};

const findNextNonEmptyLineIndex = (lines: string[], startIndex: number): number => {
  for (let index = startIndex; index < lines.length; index += 1) {
    if (trimLine(lines[index] ?? '').length > 0) {
      return index;
    }
  }
  return -1;
};

const isStructuredLine = (value: string): boolean => (
  headerLinePattern.test(value)
  || alertLinePattern.test(value)
  || orderedListItemPattern.test(value)
  || orderedListMarkerOnlyPattern.test(value)
  || unorderedListItemPattern.test(value)
  || unorderedListMarkerOnlyPattern.test(value)
);

const canMergeWithContinuation = (value: string): boolean => {
  const trimmed = trimLine(value);
  if (!trimmed) {
    return false;
  }
  if (rawUrlPattern.test(trimmed)) {
    return false;
  }
  return !isStructuredLine(trimmed);
};

const canMergeLabelWithValue = (value: string): boolean => {
  const trimmed = trimLine(value);
  if (!canMergeWithContinuation(trimmed)) {
    return false;
  }
  return trimmed.length <= 160;
};

const normalizeAssistantText = (text: string): string[] => {
  const rawLines = text.split('\n');
  const normalizedLines: string[] = [];

  for (let index = 0; index < rawLines.length; index += 1) {
    const current = trimLine(rawLines[index] ?? '');

    if (!current) {
      if (normalizedLines[normalizedLines.length - 1] !== '') {
        normalizedLines.push('');
      }
      continue;
    }

    const nextIndex = findNextNonEmptyLineIndex(rawLines, index + 1);
    const nextLine = nextIndex >= 0 ? trimLine(rawLines[nextIndex] ?? '') : '';

    if (orderedListMarkerOnlyPattern.test(current) && canMergeWithContinuation(nextLine)) {
      normalizedLines.push(`1. ${nextLine}`);
      index = nextIndex;
      continue;
    }

    if (unorderedListMarkerOnlyPattern.test(current) && canMergeWithContinuation(nextLine)) {
      normalizedLines.push(`- ${nextLine}`);
      index = nextIndex;
      continue;
    }

    const labelOnlyMatch = current.match(labelOnlyPattern);
    if (labelOnlyMatch && canMergeLabelWithValue(nextLine)) {
      normalizedLines.push(`${labelOnlyMatch[1]}: ${nextLine}`);
      index = nextIndex;
      continue;
    }

    normalizedLines.push(current);
  }

  while (normalizedLines[normalizedLines.length - 1] === '') {
    normalizedLines.pop();
  }

  return normalizedLines;
};

const parseMarkdownLinks = (text: string, styles: MessageFormatStyles, keyPrefix: string) => {
  const output: React.ReactNode[] = [];
  let lastIndex = 0;
  let matchIndex = 0;
  markdownLinkPattern.lastIndex = 0;

  let match = markdownLinkPattern.exec(text);
  while (match) {
    const [fullMatch, label, href] = match;
    if (match.index > lastIndex) {
      output.push(text.slice(lastIndex, match.index));
    }
    if (isSafeHref(href)) {
      const isExternal = /^https?:\/\//.test(href);
      output.push(
        <a
          key={`${keyPrefix}-link-${matchIndex}`}
          href={href}
          className={styles.inlineLink}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noreferrer' : undefined}
        >
          {label}
        </a>
      );
    } else {
      output.push(fullMatch);
    }
    lastIndex = match.index + fullMatch.length;
    matchIndex += 1;
    match = markdownLinkPattern.exec(text);
  }

  if (lastIndex < text.length) {
    output.push(text.slice(lastIndex));
  }

  return output.length > 0 ? output : [text];
};

const parseMarkdownBoldAndLinks = (
  text: string,
  styles: MessageFormatStyles,
  keyPrefix: string,
): React.ReactNode[] => {
  const output: React.ReactNode[] = [];
  let lastIndex = 0;
  let matchIndex = 0;
  markdownBoldPattern.lastIndex = 0;

  let match = markdownBoldPattern.exec(text);
  while (match) {
    const [fullMatch, label] = match;
    if (match.index > lastIndex) {
      output.push(...parseMarkdownLinks(text.slice(lastIndex, match.index), styles, `${keyPrefix}-plain-${matchIndex}`));
    }
    output.push(
      <span key={`${keyPrefix}-bold-${matchIndex}`} className={styles.boldText}>
        {label}
      </span>,
    );
    lastIndex = match.index + fullMatch.length;
    matchIndex += 1;
    match = markdownBoldPattern.exec(text);
  }

  if (lastIndex < text.length) {
    output.push(...parseMarkdownLinks(text.slice(lastIndex), styles, `${keyPrefix}-plain-${matchIndex}`));
  }

  return output.length > 0 ? output : parseMarkdownLinks(text, styles, `${keyPrefix}-plain`);
};

const parseInlineTokens = <TCitation,>(
  text: string,
  citations: TCitation[],
  onCitationClick: ((citation: TCitation) => void) | undefined,
  styles: MessageFormatStyles,
): React.ReactNode[] => {
  const parts = text.split(citationTokenPattern).filter(Boolean);
  return parts.flatMap((part, index) => {
    const boldMatch = part.match(/^\*\*([\s\S]+)\*\*$/);
    if (boldMatch) {
      return (
        <span key={`bold-${index}`} className={styles.boldText}>
          {boldMatch[1]}
        </span>
      );
    }

    const singleSourceMatch = part.match(/^\[Source\s+(\d+)\]$/);
    if (singleSourceMatch) {
      const sourceNum = Number(singleSourceMatch[1]);
      const citation = citations[sourceNum - 1];
      if (!citation || !onCitationClick) {
        return part;
      }
      return (
        <button
          key={`cite-${index}`}
          type="button"
          className={styles.citationLink}
          onClick={() => onCitationClick(citation)}
          data-testid={`ai-citation-${sourceNum}`}
        >
          [Source {sourceNum}]
        </button>
      );
    }

    return parseMarkdownBoldAndLinks(part, styles, `text-${index}`);
  });
};

const parseLabelValueLine = (line: string): LabelValueBlock | null => {
  if (rawUrlPattern.test(line)) {
    return null;
  }
  const colonIndex = line.indexOf(':');
  if (colonIndex <= 0 || colonIndex >= line.length - 1) {
    return null;
  }
  const label = collapseWhitespace(line.slice(0, colonIndex));
  const value = collapseWhitespace(line.slice(colonIndex + 1));
  if (!label || !value) {
    return null;
  }
  if (!labelOnlyPattern.test(`${label}:`)) {
    return null;
  }
  return {
    kind: 'labelValue',
    label,
    value,
  };
};

const shouldRenderAsCompactHeader = (content: string): boolean => {
  const normalized = collapseWhitespace(content);
  if (!normalized || normalized.length > compactHeaderMaxLength) {
    return false;
  }
  if (/[.!?]$/.test(normalized)) {
    return false;
  }
  const wordCount = normalized.split(/\s+/).filter(Boolean).length;
  return wordCount <= 6;
};

const classifyMessageBlocks = (lines: string[]): MessageBlock[] => {
  const blocks: MessageBlock[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';
    if (!line) {
      blocks.push({ kind: 'spacer' });
      continue;
    }

    const headerMatch = line.match(headerLinePattern);
    if (headerMatch) {
      const content = collapseWhitespace(headerMatch[1] ?? '');
      if (!shouldRenderAsCompactHeader(content)) {
        blocks.push({
          kind: 'paragraph',
          content,
        });
        continue;
      }
      blocks.push({
        kind: 'header',
        content,
      });
      continue;
    }

    const alertMatch = line.match(alertLinePattern);
    if (alertMatch) {
      blocks.push({
        kind: 'alert',
        content: collapseWhitespace(alertMatch[1] ?? ''),
      });
      continue;
    }

    const noteMatch = line.match(noteLinePattern);
    if (noteMatch) {
      blocks.push({
        kind: 'note',
        content: collapseWhitespace(noteMatch[1] ?? ''),
      });
      continue;
    }

    const orderedListMatch = line.match(orderedListItemPattern);
    if (orderedListMatch) {
      const items: string[] = [];
      let cursor = index;
      while (cursor < lines.length) {
        const candidate = lines[cursor] ?? '';
        const match = candidate.match(orderedListItemPattern);
        if (!match) {
          break;
        }
        items.push(collapseWhitespace(match[2] ?? ''));
        cursor += 1;
      }
      blocks.push({ kind: 'orderedList', items });
      index = cursor - 1;
      continue;
    }

    const unorderedListMatch = line.match(unorderedListItemPattern);
    if (unorderedListMatch) {
      const items: string[] = [];
      let cursor = index;
      while (cursor < lines.length) {
        const candidate = lines[cursor] ?? '';
        const match = candidate.match(unorderedListItemPattern);
        if (!match) {
          break;
        }
        items.push(collapseWhitespace(match[1] ?? ''));
        cursor += 1;
      }
      blocks.push({ kind: 'unorderedList', items });
      index = cursor - 1;
      continue;
    }

    const labelValue = parseLabelValueLine(line);
    if (labelValue) {
      blocks.push(labelValue);
      continue;
    }

    blocks.push({
      kind: 'paragraph',
      content: line,
    });
  }

  return blocks;
};

const renderListBlock = <TCitation,>(
  block: OrderedListBlock | UnorderedListBlock,
  citations: TCitation[],
  onCitationClick: ((citation: TCitation) => void) | undefined,
  styles: MessageFormatStyles,
  key: string,
): React.ReactNode => {
  const ListTag = block.kind === 'orderedList' ? 'ol' : 'ul';
  return (
    <ListTag key={key} className={styles.numberedList}>
      {block.items.map((item, index) => (
        <li key={`${key}-item-${index}`} className={styles.listItem}>
          <span className={styles.listContent}>
            <span className={styles.listIndex}>{block.kind === 'orderedList' ? `${index + 1}.` : '•'}</span>{' '}
            {parseInlineTokens(item, citations, onCitationClick, styles)}
          </span>
        </li>
      ))}
    </ListTag>
  );
};

const renderMessageBlocks = <TCitation,>(
  blocks: MessageBlock[],
  citations: TCitation[],
  onCitationClick: ((citation: TCitation) => void) | undefined,
  styles: MessageFormatStyles,
): React.ReactNode[] => blocks.map((block, index) => {
  const key = `block-${index}`;
  switch (block.kind) {
    case 'spacer':
      return <div key={key} className={styles.messageSpacer} />;
    case 'header':
      return (
        <div key={key} className={styles.messageHeader}>
          {parseInlineTokens(block.content, citations, onCitationClick, styles)}
        </div>
      );
    case 'alert':
      return (
        <div key={key} className={styles.alertBox}>
          <div className={styles.alertIcon}>
            <AlertTriangle size={14} />
          </div>
          <div className={styles.alertContent}>
            {parseInlineTokens(block.content, citations, onCitationClick, styles)}
          </div>
        </div>
      );
    case 'note':
      return (
        <div key={key} className={styles.noteBox}>
          <div className={styles.noteContent}>
            {parseInlineTokens(block.content, citations, onCitationClick, styles)}
          </div>
        </div>
      );
    case 'labelValue':
      return (
        <div key={key} className={styles.labelRow}>
          <span className={styles.labelText}>
            {parseInlineTokens(block.label, citations, onCitationClick, styles)}
            <span className={styles.labelSeparator}>:</span>
          </span>
          <span className={styles.labelValue}>
            {parseInlineTokens(block.value, citations, onCitationClick, styles)}
          </span>
        </div>
      );
    case 'orderedList':
    case 'unorderedList':
      return renderListBlock(block, citations, onCitationClick, styles, key);
    case 'paragraph':
    default:
      return (
        <p key={key} className={`${styles.messageLine} ${styles.chatBubbleParagraph}`}>
          {parseInlineTokens(block.content, citations, onCitationClick, styles)}
        </p>
      );
  }
});

export const formatMessage = <TCitation,>(
  text: string,
  citations: TCitation[],
  onCitationClick: ((citation: TCitation) => void) | undefined,
  styles: MessageFormatStyles,
): React.ReactNode[] => {
  const normalizedLines = normalizeAssistantText(rehydrateCollapsedStructure(stripMachinePayloadArtifacts(text)));
  const blocks = classifyMessageBlocks(normalizedLines);
  return renderMessageBlocks(blocks, citations, onCitationClick, styles);
};
